"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { logger } from "@/lib/logger";
import { StorageManager } from "@/lib/storage/local-storage";

/**
 * @file 汎用的なLocalStorage永続化フック
 *
 * 任意の状態をLocalStorageに保存・復元するための汎用カスタムフック。
 * SSR対応、エラーハンドリング、型安全性を提供。
 *
 * 機能:
 * - 型安全なLocalStorageアクセス
 * - SSR対応（useEffectでクライアントサイドのみアクセス）
 * - エラーハンドリング（LocalStorage無効時の graceful fallback）
 * - debounceによる書き込み最適化
 * - バージョニングサポート
 */

/** デバウンスのデフォルト遅延時間（ミリ秒） */
const DEFAULT_DEBOUNCE_DELAY = 50;

/** useLocalStorageのオプション */
export interface UseLocalStorageOptions<T> {
  /** デフォルト値 */
  defaultValue: T;
  /** データのバージョン（マイグレーション用） */
  version?: number;
  /** 書き込みのdebounce時間（ミリ秒） */
  debounceDelay?: number;
  /** データのバリデーション関数 */
  validate?: (value: unknown) => value is T;
  /** バージョンアップ時のマイグレーション関数 */
  migrate?: (oldData: unknown, oldVersion: number) => T;
}

/** 保存されるデータの構造 */
interface StoredData<T> {
  version: number;
  data: T;
}

function isStoredData<T>(value: unknown): value is StoredData<T> {
  if (!value || typeof value !== "object") return false;
  return "version" in value && "data" in value;
}

/**
 * 汎用的なLocalStorage永続化フック
 *
 * @param key - LocalStorageのキー
 * @param options - オプション設定
 * @returns [現在の値, 値を更新する関数]
 *
 * @example
 * ```tsx
 * // シンプルな使用例
 * const [theme, setTheme, isLoading] = useLocalStorage('theme', {
 *   defaultValue: 'light'
 * });
 *
 * // バージョニングとマイグレーション付き
 * const [settings, setSettings] = useLocalStorage('settings', {
 *   defaultValue: { notifications: true },
 *   version: 2,
 *   migrate: (oldData, oldVersion) => {
 *     if (oldVersion === 1) {
 *       return { ...oldData, notifications: true };
 *     }
 *     return { notifications: true };
 *   }
 * });
 * ```
 *
 * @note 同じキーで複数のコンポーネントがこのフックを使用する場合、
 * メモリキャッシュにより効率的にデータが共有されます
 */
export function useLocalStorage<T>(
  key: string,
  options: UseLocalStorageOptions<T>,
): [T, (value: T | ((prev: T) => T)) => void] {
  const {
    defaultValue,
    version = 1,
    debounceDelay = DEFAULT_DEBOUNCE_DELAY,
    validate,
    migrate,
  } = options;

  const readStoredValue = useCallback((): T => {
    if (typeof window === "undefined") return defaultValue;
    try {
      const stored = StorageManager.getJSON<StoredData<T>>(key);
      if (!stored) return defaultValue;
      if (!isStoredData<T>(stored)) {
        logger.warn(
          `Invalid data structure for ${key}. Missing data property. Using default.`,
        );
        return defaultValue;
      }
      if (stored.version !== version) {
        if (!migrate) {
          logger.warn(
            `Version mismatch for ${key}. Expected ${version}, got ${stored.version}. Using default.`,
          );
          return defaultValue;
        }
        logger.info(
          `Migrating ${key} from version ${stored.version} to ${version}`,
        );
        return migrate(stored.data, stored.version);
      }
      if (stored.data === null || stored.data === undefined) {
        logger.warn(`Null or undefined data for ${key}. Using default.`);
        return defaultValue;
      }
      if (validate && !validate(stored.data)) {
        logger.warn(`Invalid data for ${key}. Using default.`);
        return defaultValue;
      }
      return stored.data;
    } catch (error) {
      logger.error(`Error loading ${key} from localStorage:`, error);
      return defaultValue;
    }
  }, [defaultValue, key, migrate, validate, version]);

  // 初期状態を遅延初期化で取得（SSR対応とパフォーマンス向上）
  const [value, setValue] = useState<T>(readStoredValue);

  const saveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const latestValueRef = useRef<T>(value);
  const isDirtyRef = useRef(false);

  // LocalStorageに保存する関数
  const saveToStorage = useCallback(
    (newValue: T) => {
      try {
        const dataToStore: StoredData<T> = {
          version,
          data: newValue,
        };
        StorageManager.setJSON(key, dataToStore);
      } catch (error) {
        logger.error(`Error saving ${key} to localStorage:`, error);
      }
    },
    [key, version],
  );

  const scheduleSave = useCallback(
    (nextValue: T) => {
      latestValueRef.current = nextValue;
      isDirtyRef.current = true;

      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }

      saveTimerRef.current = setTimeout(() => {
        saveTimerRef.current = null;
        if (isDirtyRef.current) {
          saveToStorage(latestValueRef.current);
          isDirtyRef.current = false;
        }
      }, debounceDelay);
    },
    [debounceDelay, saveToStorage],
  );

  const flushPendingSave = useCallback(() => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    if (isDirtyRef.current) {
      saveToStorage(latestValueRef.current);
      isDirtyRef.current = false;
    }
  }, [saveToStorage]);

  // 値を更新する関数（debounce付き）
  const updateValue = useCallback(
    (newValue: T | ((prev: T) => T)) => {
      setValue((prev) => {
        const nextValue =
          typeof newValue === "function"
            ? (newValue as (prev: T) => T)(prev)
            : newValue;

        scheduleSave(nextValue);

        return nextValue;
      });
    },
    [scheduleSave],
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handlePageHide = () => flushPendingSave();
    window.addEventListener("beforeunload", handlePageHide);
    window.addEventListener("pagehide", handlePageHide);
    return () => {
      window.removeEventListener("beforeunload", handlePageHide);
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, [flushPendingSave]);

  // クリーンアップ
  useEffect(() => {
    return () => {
      flushPendingSave();
    };
  }, [flushPendingSave]);

  return [value, updateValue];
}
